import {
  adminCableVars,
  defaultCableVars,
  deleteCables,
  ecopetrolCableVars,
  kocCableVars,
  restoreCables
} from "@/models/equipment/cables/predicates";
import type { Cables } from "gql/api";
import {
  useCablesQuery,
  useDeleteCablesMutation,
  useInsertCableMutation,
  useRestoreCablesMutation,
  useUpdateCableByPkMutation
} from "gql/api";
import { v4 } from "uuid";
import { computed } from "vue";
import { useEquipment } from "@/composables/equipment/baseEquipment";
import type { UseEquipment } from "@/composables/equipment/equipmentTypes";
import { cableTableColumns } from "./cableTableColumns";
import useUserInfo from "@/composables/auth/useUserInfo";

export function useCables(isAdmin: boolean): UseEquipment<Cables> {
  const baseEquipment = useEquipment<Cables>(isAdmin);
  const title = "Cables";
  const user = useUserInfo();

  const getVariables = () => {
    if (user.is_koc_customer) return kocCableVars;
    if (user.is_ecopetrol_customer) return ecopetrolCableVars;
    if (user.is_admin) return adminCableVars;
    return defaultCableVars;
  };

  const {
    data,
    executeQuery: refetch,
    stale: isStale
  } = useCablesQuery({
    variables: getVariables()
  });

  const allRows = computed(() => data.value?.cables as Cables[] | undefined);

  const hashOrder: (keyof Cables)[] = [
    "manufacturer",
    "part_number",
    "armor",
    "construction",
    "family",
    "gauge",
    "amp_factor"
  ];

  const { defineVisibleColumns, defineFilterData } = baseEquipment;

  const { defineUpsert, defineRestore, defineDelete } =
    baseEquipment.defineMutationHandlers(refetch, hashOrder);

  const { executeMutation: executeInsertCable } = useInsertCableMutation();
  const { executeMutation: executeUpdateCable } = useUpdateCableByPkMutation();
  const { executeMutation: executeDeleteCable } = useDeleteCablesMutation();
  const { executeMutation: executeRestoreCable } = useRestoreCablesMutation();

  const insertCable = (cable: Omit<Cables, "sizing_import" | "user">) =>
    executeInsertCable({ object: cable });

  const updateCable = (id: string, cable: Cables) =>
    executeUpdateCable({ id, object: cable });

  const deleteCable = (ids: string[]) =>
    executeDeleteCable({ where: deleteCables(ids) });

  const restoreCable = (ids: string[]) =>
    executeRestoreCable({ where: restoreCables(ids) });

  baseEquipment.defineEmptyRow(() => ({
    __typename: "cables",
    id: v4(),
    manufacturer: "",
    part_number: "",
    armor: "",
    construction: "",
    family: "",
    gauge: "",
    max_temperature_fahrenheit: null,
    width_inches: null,
    depth_inches: null,
    kv: null,
    amp_factor: null,
    resistance_per_1000_ft: null,
    impedance_60_hz_per_1000_ft: null,
    created_at: "now()",
    updated_at: "now()",
    deleted_at: null,
    imported_sizing_id: null,
    pound_per_foot: null
  }));

  return {
    searchPlaceholder: "Search Cables...",
    searchToolTip: `Add quotes around search terms for a more exact result. Ex: "SELF"`,
    columns: defineVisibleColumns(cableTableColumns),
    handleUpsert: defineUpsert(insertCable, updateCable),
    handleDelete: defineDelete(deleteCable),
    handleRestore: defineRestore(restoreCable),
    totalCount: computed(() => data.value?.cables_aggregate.aggregate?.count),
    title,
    allRows,
    isAdmin,
    refetch,
    isStale,
    ...baseEquipment,
    ...defineFilterData({
      columns: cableTableColumns,
      globalSearchFields: ["family", "part_number", "gauge"],
      allRows,
      title
    })
  };
}
